import type { SVGProps } from 'react'

/**
 * SPEC-016 Album Studio — the single stroke icon set (ADR-084). One 20×20
 * viewBox, `currentColor`, so every icon inherits the token color of its
 * container. Ported verbatim from the signed-off design's icon set; kept in one
 * place so the list, overview, and shell never repeat SVG paths.
 */

type IconProps = SVGProps<SVGSVGElement>

const base = (props: IconProps): IconProps => ({
  viewBox: '0 0 20 20',
  width: '1em',
  height: '1em',
  'aria-hidden': true,
  focusable: false,
  ...props,
})

export const Icon = {
  grid: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3" y="3" width="6" height="6" rx="1.5" />
      <rect x="11" y="3" width="6" height="6" rx="1.5" />
      <rect x="3" y="11" width="6" height="6" rx="1.5" />
      <rect x="11" y="11" width="6" height="6" rx="1.5" />
    </svg>
  ),
  disc: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="10" cy="10" r="7" />
      <circle cx="10" cy="10" r="2.1" />
    </svg>
  ),
  rocket: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M10 2.5c3 1 4.5 3.5 4.5 7 0 1.6-.5 3-1.3 4.2H6.8C6 12.5 5.5 11.1 5.5 9.5c0-3.5 1.5-6 4.5-7z" strokeLinejoin="round" />
      <circle cx="10" cy="8.5" r="1.4" />
      <path d="M6.8 13.7 5 16m8.2-2.3L15 16M8.3 15.3 8 17.5m3.7-2.2.3 2.2" strokeLinecap="round" />
    </svg>
  ),
  link: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M8.5 11.5a3 3 0 0 0 4.2 0l2-2a3 3 0 0 0-4.2-4.2l-1 1" strokeLinecap="round" />
      <path d="M11.5 8.5a3 3 0 0 0-4.2 0l-2 2a3 3 0 0 0 4.2 4.2l1-1" strokeLinecap="round" />
    </svg>
  ),
  plus: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10 4.5v11M4.5 10h11" strokeLinecap="round" />
    </svg>
  ),
  pencil: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M13.2 4.3l2.5 2.5M4 16l1-3.4 8-8 2.5 2.5-8 8L4 16z" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  ),
  trash: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M4.5 6h11M8 6V4.6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1V6M6 6l.7 9a1.4 1.4 0 0 0 1.4 1.3h3.8a1.4 1.4 0 0 0 1.4-1.3L15 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  chev: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M7.5 5l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  chevDown: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M5 7.5l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  sort: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M6 4.5v11M6 15.5l-2-2M6 15.5l2-2M14 15.5v-11M14 4.5l-2 2M14 4.5l2 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  filter: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M3.5 5h13l-5 6v4l-3 1.5V11l-5-6z" strokeLinejoin="round" />
    </svg>
  ),
  check: (p: IconProps) => (
    <svg {...base(p)} fill="currentColor">
      <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4A1 1 0 014.7 9.3L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" />
    </svg>
  ),
  warn: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M10 7v4.5" strokeLinecap="round" />
      <circle cx="10" cy="14.3" r=".2" fill="currentColor" stroke="none" />
      <path d="M8.6 3.7 2.5 14.5A1.6 1.6 0 004 17h12a1.6 1.6 0 001.4-2.5L11.4 3.7a1.6 1.6 0 00-2.8 0z" strokeLinejoin="round" />
    </svg>
  ),
  x: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
    </svg>
  ),
  search: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.7">
      <circle cx="9" cy="9" r="5.25" />
      <path d="M13 13l3.5 3.5" strokeLinecap="round" />
    </svg>
  ),
  music: (p: IconProps) => (
    <svg {...base(p)} fill="currentColor">
      <path d="M10 3.75a.75.75 0 01.75.75v6.5a3.25 3.25 0 11-1.5-2.74V4.5A.75.75 0 0110 3.75z" />
    </svg>
  ),
  doc: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M6 2.5h5.5L16 7v8.5a2 2 0 01-2 2H6a2 2 0 01-2-2v-11a2 2 0 012-2z" />
      <path d="M11 2.5V7h4" strokeLinejoin="round" />
    </svg>
  ),
  image: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3.5" y="4" width="13" height="12" rx="2" />
      <circle cx="7.5" cy="8" r="1.3" />
      <path d="M4 14l3.5-3 2.5 2 3-3.5 3.5 4" strokeLinejoin="round" />
    </svg>
  ),
  mic: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="8" y="3" width="4" height="8" rx="2" />
      <path d="M5.5 9.5a4.5 4.5 0 009 0M10 14v3M8 17h4" strokeLinecap="round" />
    </svg>
  ),
  speaker: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M4 8h2.4L10 4.8v10.4L6.4 12H4z" strokeLinejoin="round" />
      <path d="M12.6 8a3.2 3.2 0 010 4M14.6 6a6 6 0 010 8" strokeLinecap="round" />
    </svg>
  ),
  stop: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="5.5" y="5.5" width="9" height="9" rx="2" />
    </svg>
  ),
  play: (p: IconProps) => (
    <svg {...base(p)} fill="currentColor">
      <path d="M6.5 4.7a1 1 0 0 1 1.53-.85l7 4.8a1 1 0 0 1 0 1.7l-7 4.8A1 1 0 0 1 6.5 14.3V4.7z" />
    </svg>
  ),
  download: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M10 4v9M6.5 9.5 10 13l3.5-3.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4.5 15h11" strokeLinecap="round" />
    </svg>
  ),
  clock: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="10" cy="10" r="6.5" />
      <path d="M10 6.3V10l2.6 1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  cal: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3.5" y="4.5" width="13" height="12" rx="2" />
      <path d="M3.5 8h13M7 3v3M13 3v3" strokeLinecap="round" />
    </svg>
  ),
  grip: (p: IconProps) => (
    <svg {...base(p)} fill="currentColor">
      <circle cx="7.5" cy="5" r="1.3" />
      <circle cx="12.5" cy="5" r="1.3" />
      <circle cx="7.5" cy="10" r="1.3" />
      <circle cx="12.5" cy="10" r="1.3" />
      <circle cx="7.5" cy="15" r="1.3" />
      <circle cx="12.5" cy="15" r="1.3" />
    </svg>
  ),
  arrow: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="2.2">
      <path d="M4 10h11M11 5l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  layers: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M10 3l6.5 3.5L10 10 3.5 6.5 10 3z" strokeLinejoin="round" />
      <path d="M3.5 10L10 13.5 16.5 10M3.5 13.5 10 17l6.5-3.5" strokeLinejoin="round" />
    </svg>
  ),
  box: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M3.5 6.5 10 3l6.5 3.5v7L10 17l-6.5-3.5v-7z" strokeLinejoin="round" />
      <path d="M3.5 6.5 10 10l6.5-3.5M10 10v7" strokeLinejoin="round" />
    </svg>
  ),
  bolt: (p: IconProps) => (
    <svg {...base(p)} fill="currentColor">
      <path d="M10.8 2 4.5 11h4l-.8 7 6.8-9.2h-4L10.8 2z" />
    </svg>
  ),
  reorder: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M6 4v9M6 13l-2-2M6 13l2-2M14 16V7M14 7l-2 2M14 7l2 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  command: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M7 4a1.5 1.5 0 100 3h6a1.5 1.5 0 100-3v9a1.5 1.5 0 100 3H7a1.5 1.5 0 100-3h6M7 7v6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  press: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h7A1.5 1.5 0 0 1 14 5.5v9a1.5 1.5 0 0 0 1.5 1.5H6a2 2 0 0 1-2-2v-8.5z" strokeLinejoin="round" />
      <path d="M14 7.5h1.5A1.5 1.5 0 0 1 17 9v5.5a1.5 1.5 0 0 1-1.5 1.5M6.5 7.5h5M6.5 10.5h5M6.5 13.5h3" strokeLinecap="round" />
    </svg>
  ),
  users: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="7.5" cy="7" r="2.6" />
      <path d="M3 16c0-2.5 2-4.2 4.5-4.2S12 13.5 12 16" strokeLinecap="round" />
      <path d="M13 5.2a2.6 2.6 0 0 1 0 5M14.2 11.9c1.6.5 2.8 2 2.8 4.1" strokeLinecap="round" />
    </svg>
  ),
  gear: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="10" cy="10" r="2.3" />
      <path d="M10 2.6l1 2.1 2.3-.5.5 2.3 2.1 1-1.1 2 1.1 2-2.1 1-.5 2.3-2.3-.5-1 2.1-1-2.1-2.3.5-.5-2.3-2.1-1 1.1-2-1.1-2 2.1-1 .5-2.3 2.3.5 1-2.1z" strokeLinejoin="round" />
    </svg>
  ),
  external: (p: IconProps) => (
    <svg {...base(p)} fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M11 4.5h4.5V9M15.5 4.5 9 11" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13 11.5V15a1.5 1.5 0 0 1-1.5 1.5h-6A1.5 1.5 0 0 1 4 15V9a1.5 1.5 0 0 1 1.5-1.5H9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
} as const

export type IconName = keyof typeof Icon
